/**
 * Go Colors reports — onboarding and the hiring-funnel stage list.
 *
 * A Selected candidate is ONBOARDED once the ARM has filled their DOJ and gross salary (their
 * salary request is `completed`). The server marks those candidates `onboarded: true` and sends
 * `summary.onboarded`; both are only present on the Go Colors (`reports`) payload.
 *
 * Onboarded is a SUBSET of Selected, not a fifth outcome:
 *   - `outcome` stays 'Selected', and every Selected count keeps including onboarded candidates;
 *   - Onboarded is shown as its own row / funnel stage beside Selected;
 *   - it is NEVER added into a Total, which stays Selected + Rejected + Dropped + Undecided.
 * (The screening page is different on purpose: there the Selected and Onboarded tabs are
 * disjoint, so a candidate is listed in exactly one of them.)
 *
 * Everything here is pure so it can be unit-tested without rendering the page.
 */

export const ONBOARDED_LABEL = 'Onboarded';
/** Row label in the Excel export, where the row sits among the four exclusive outcomes. */
export const ONBOARDED_EXPORT_LABEL = 'Onboarded (of Selected)';

/** Funnel stages in display order. `onboardingOnly` stages need onboarding data in the report. */
export const FUNNEL_STAGES = [
  { key: 'applied', label: 'Applied' },
  { key: 'interviewsDone', label: 'Interviews' },
  { key: 'selected', label: 'Selected' },
  { key: 'onboarded', label: ONBOARDED_LABEL, onboardingOnly: true },
  { key: 'dropped', label: 'Dropped' },
];

/** True when the report payload carries onboarding data (Go Colors only). */
export function hasOnboarding(report) {
  return Number.isFinite(report?.summary?.onboarded);
}

/** True for a candidate row that is Selected and onboarded. */
export function isOnboardedCandidate(candidate) {
  return candidate?.outcome === 'Selected' && candidate?.onboarded === true;
}

/** The funnel stages this report can show. */
export function funnelStagesFor(report) {
  return hasOnboarding(report) ? FUNNEL_STAGES : FUNNEL_STAGES.filter((s) => !s.onboardingOnly);
}

/** Whole-report funnel: `[{ key, label, count }]`, for the single-column panel and its chart. */
export function buildSingleFunnelStages(report) {
  const summary = report?.summary || {};
  const counts = {
    applied: summary.totalCandidates || 0,
    interviewsDone: (report?.rawCandidates || []).filter((c) => c.interviewScore != null).length,
    selected: summary.selected || 0,
    onboarded: summary.onboarded || 0,
    dropped: summary.dropped || 0,
  };
  return funnelStagesFor(report).map((s) => ({ ...s, count: counts[s.key] }));
}

/**
 * The server's `report.funnel` (`[{ stage, count }]`) with an Onboarded stage directly after
 * Selected. Returned unchanged when the report has no onboarding data or no Selected stage.
 */
export function withOnboardedFunnelStage(funnel, report) {
  const rows = Array.isArray(funnel) ? funnel : [];
  const at = rows.findIndex((row) => row.stage === 'Selected');
  if (!hasOnboarding(report) || at === -1) return rows;
  return [
    ...rows.slice(0, at + 1),
    { stage: ONBOARDED_LABEL, count: report.summary.onboarded },
    ...rows.slice(at + 1),
  ];
}

/**
 * The server's `report.outcomeBreakdown` (`[{ name, value, pct }]`) with an Onboarded row
 * directly after Selected. `pct` is the share of all candidates, like the other rows.
 */
export function withOnboardedOutcome(outcomeBreakdown, report) {
  const rows = Array.isArray(outcomeBreakdown) ? outcomeBreakdown : [];
  const at = rows.findIndex((row) => row.name === 'Selected');
  if (!hasOnboarding(report) || at === -1) return rows;
  const total = report.summary.totalCandidates || 0;
  const value = report.summary.onboarded;
  const pct = total ? Number(((value / total) * 100).toFixed(1)) : 0;
  return [
    ...rows.slice(0, at + 1),
    { name: ONBOARDED_EXPORT_LABEL, value, pct },
    ...rows.slice(at + 1),
  ];
}

/** Empty per-column funnel counters, one per stage key. */
export function emptyFunnelCounts() {
  return FUNNEL_STAGES.reduce((acc, s) => {
    acc[s.key] = 0;
    return acc;
  }, {});
}

/** Adds one candidate row to a per-column funnel counter made by `emptyFunnelCounts()`. */
export function addToFunnel(counts, candidate) {
  const outcome = candidate?.outcome || 'Undecided';
  counts.applied += 1;
  if (candidate?.interviewScore != null) counts.interviewsDone += 1;
  if (outcome === 'Selected') counts.selected += 1;
  if (isOnboardedCandidate(candidate)) counts.onboarded += 1;
  if (outcome === 'Dropped') counts.dropped += 1;
  return counts;
}
